import type { WalletError, WalletName } from '@solana/wallet-adapter-base';
import {
	BaseSignerWalletAdapter,
	WalletConnectionError,
	WalletDisconnectionError,
	WalletNotConnectedError,
	WalletNotReadyError,
	WalletReadyState,
	WalletSignMessageError,
	WalletSignTransactionError,
} from '@solana/wallet-adapter-base';
import type { Transaction, TransactionVersion, VersionedTransaction } from '@solana/web3.js';
import { WalletConnectSolanaCore } from './SolanaCore';
import { SignClientTypes } from '@walletconnect/types';
import Client from './Client';
import { makeObservable, reaction } from 'mobx';
import type ExplorerRootStore from '../../ExplorerRootStore';
import { flowSingle } from '../../utils';

export type WalletConnectWalletAdapterConfig = {
	options: SignClientTypes.Options;
	client?: Client;
};

export class WalletConnectWalletAdapter extends BaseSignerWalletAdapter {
	#root: ExplorerRootStore;
	readonly name = 'WalletConnect' as WalletName<'WalletConnect'>;
	readonly url = 'https://walletconnect.org';
	readonly icon = 'https://cdn.mayan.finance/stores/wallet-connect.png';
	readonly supportedTransactionVersions: ReadonlySet<TransactionVersion> = new Set(['legacy', 0]);

	private _connecting: boolean = false;
	private _wallet: WalletConnectSolanaCore | undefined;

	constructor(root: ExplorerRootStore) {
		super();
		this.#root = root;
		makeObservable(this, {}, {});

		reaction(
			() => this.client,
			(next, prev) => {
				if (prev && !next) {
					this.emit('disconnect');
				}
			},
		);

		this._connecting = false;
	}

	get publicKey() {
		return this._wallet?.publicKey ?? null;
	}

	get connecting() {
		return this._connecting;
	}

	get provider() {
		return this.client?.solProvider;
	}

	get client() {
		return this.#root.wallets?.walletconnect;
	}

	get readyState() {
		if (!this.client) return WalletReadyState.NotDetected;
		return typeof window === 'undefined' ? WalletReadyState.Unsupported : WalletReadyState.Loadable;
	}

	connect = flowSingle(function* (this: WalletConnectWalletAdapter) {
		if (this.connected || this.connecting) {
			return;
		}

		this._connecting = true;
		try {
			if (this.readyState !== WalletReadyState.Loadable) {
				throw new WalletNotReadyError();
			}
			yield this.client?.connectSolana();

			if (!this.provider) {
				throw new WalletConnectionError();
			}
			this._wallet = new WalletConnectSolanaCore(this.provider);
			this.emit('connect', this.publicKey!);
		} catch (error: unknown) {
			this._connecting = false;
			throw error;
		} finally {
			this._connecting = false;
		}
	});

	async disconnect(): Promise<void> {
		const wallet = this._wallet;
		if (wallet) {
			this._wallet = undefined;
			try {
				await this.client?.disconnectSolana();
			} catch (error: unknown) {
				console.error('WalletConnectWalletAdapter: disconnect error', error);
				this.emit('error', new WalletDisconnectionError((error as Error)?.message, error));
			}
		}
		this.emit('disconnect');
	}

	async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
		try {
			const wallet = this._wallet;
			if (!wallet) throw new WalletNotConnectedError();
			try {
				return await wallet.signTransaction(transaction);
			} catch (error: unknown) {
				throw new WalletSignTransactionError((error as Error)?.message, error);
			}
		} catch (error: unknown) {
			this.emit('error', error as WalletError);
			throw error;
		}
	}

	async signMessage(message: Uint8Array): Promise<Uint8Array> {
		try {
			const wallet = this._wallet;
			if (!wallet) throw new WalletNotConnectedError();
			try {
				return await wallet.signMessage(message);
			} catch (error: unknown) {
				throw new WalletSignMessageError((error as Error)?.message, error);
			}
		} catch (error: unknown) {
			this.emit('error', error as WalletError);
			throw error;
		}
	}

	async signAndSendTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<string> {
		try {
			const wallet = this._wallet;
			if (!wallet) throw new WalletNotConnectedError();
			try {
				return await wallet.signAndSendTransaction(transaction);
			} catch (error: unknown) {
				throw new WalletSignTransactionError((error as Error)?.message, error);
			}
		} catch (error: unknown) {
			this.emit('error', error as WalletError);
			throw error;
		}
	}

	async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
		try {
			const wallet = this._wallet;
			if (!wallet) throw new WalletNotConnectedError();
			try {
				return await wallet.signAllTransactions(transactions);
			} catch (error: unknown) {
				throw new WalletSignTransactionError((error as Error)?.message, error);
			}
		} catch (error: unknown) {
			this.emit('error', error as WalletError);
			throw error;
		}
	}
}
